'use client';

import React, { useState, useMemo } from 'react';
import ContactsHeader from './ContactsHeader';
import ContactsFilterPanel from './ContactsFilterPanel';
import ContactsTable from './ContactsTable';
import ContactsBulkBar from './ContactsBulkBar';
import AIAssistantPanel from './AIAssistantPanel';
import { mockContacts, Contact } from './mockContacts';

export interface FilterState {
  search: string;
  event: string;
  stage: string;
  linkedinStatus: string;
  priority: string;
  industry: string;
  meetingStatus: string;
}

const defaultFilters: FilterState = {
  search: '',
  event: '',
  stage: '',
  linkedinStatus: '',
  priority: '',
  industry: '',
  meetingStatus: '',
};

export default function ContactsListContent() {
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(true);
  const [sortKey, setSortKey] = useState<keyof Contact>('priorityScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [aiContact, setAiContact] = useState<Contact | null>(null);

  const filtered = useMemo(() => {
    let data = [...mockContacts];
    if (filters.search) {
      const q = filters.search.toLowerCase();
      data = data.filter(
        (c) =>
          c.firstName.toLowerCase().includes(q) ||
          c.lastName.toLowerCase().includes(q) ||
          c.company.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.title.toLowerCase().includes(q)
      );
    }
    if (filters.event) data = data.filter((c) => c.event === filters.event);
    if (filters.stage) data = data.filter((c) => c.outreachStage === filters.stage);
    if (filters.linkedinStatus) data = data.filter((c) => c.linkedinStatus === filters.linkedinStatus);
    if (filters.priority) data = data.filter((c) => c.priority === filters.priority);
    if (filters.industry) data = data.filter((c) => c.industry === filters.industry);
    if (filters.meetingStatus) data = data.filter((c) => c.meetingStatus === filters.meetingStatus);

    data.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? '');
      const bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return data;
  }, [filters, sortKey, sortDir]);

  const toggleSort = (key: keyof Contact) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((c) => c.id));
    }
  };

  return (
    <div className="flex h-full min-h-screen bg-background">
      {/* Filter panel */}
      {filterOpen && (
        <ContactsFilterPanel
          filters={filters}
          onChange={(k, v) => setFilters((prev) => ({ ...prev, [k]: v }))}
          onClear={() => setFilters(defaultFilters)}
          contacts={mockContacts}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <ContactsHeader
          total={mockContacts.length}
          filtered={filtered.length}
          filterOpen={filterOpen}
          onToggleFilter={() => setFilterOpen(!filterOpen)}
          filters={filters}
          onSearchChange={(v) => setFilters((prev) => ({ ...prev, search: v }))}
        />

        <div className="flex-1 overflow-auto relative">
          {selectedIds.length > 0 && (
            <ContactsBulkBar
              count={selectedIds.length}
              onClear={() => setSelectedIds([])}
            />
          )}
          <ContactsTable
            contacts={filtered}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onToggleAll={toggleAll}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={toggleSort}
            onOpenAI={(contact) => setAiContact(contact)}
          />
        </div>
      </div>

      {/* AI Assistant Panel */}
      {aiContact && (
        <AIAssistantPanel
          contact={aiContact}
          onClose={() => setAiContact(null)}
        />
      )}
    </div>
  );
}